import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PlatformAuthProvider, usePlatformAuth } from './contexts/PlatformAuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Layout from './components/Layout';
import Login from './components/Login';
// Dashboard (the static summary page) is unmounted in favor of Live Dashboard
// below — kept in the tree for future use, not deleted.
// import Dashboard from './components/Dashboard';
import Users from './components/Users';
import Residents from './components/Residents';
import Reports from './components/Reports';
import Categories from './components/Categories';
import Departments from './components/Departments';
import Escalations from './components/Escalations';
import Map from './components/Map';
import Messaging from './pages/Messaging';
import GISAnalytics from './pages/GISAnalytics';
import LiveDashboard from './pages/LiveDashboard';
import TechnicianLocations from './pages/TechnicianLocations';
import PushNotifications from './pages/PushNotifications';
import AuditTrail from './pages/AuditTrail';
import FeedbackSystem from './pages/FeedbackSystem';
import Profile from './pages/Profile';
import UserSessions from './pages/UserSessions';
import CouncilPacks from './pages/CouncilPacks';
import ComplianceReports from './pages/ComplianceReports';
import Assignments from './components/Assignments';
import AdminRegister from './pages/AdminRegister';
import ServiceDeliveryHub from './pages/ServiceDeliveryHub';
import ResolutionReviews from './pages/ResolutionReviews';
import ContractorPerformance from './pages/ContractorPerformance';
import PublicServiceStatus from './pages/PublicServiceStatus';
import FindMunicipality from './pages/FindMunicipality';
import WardCouncillorPortal from './pages/WardCouncillorPortal';
import ContractorPortal from './pages/ContractorPortal';
import CouncillorLayout from './components/CouncillorLayout';
import ContractorLayout from './components/ContractorLayout';
import CaseWorkspace from './pages/CaseWorkspace';
import PlatformLogin from './pages/platform/PlatformLogin';
import PlatformLayout from './pages/platform/PlatformLayout';
import PlatformDashboard from './pages/platform/PlatformDashboard';
import PlatformMunicipalityDetail from './pages/platform/PlatformMunicipalityDetail';
import PlatformAuditLog from './pages/platform/PlatformAuditLog';
import PlatformBilling from './pages/platform/PlatformBilling';
import PilotEvidenceDashboard from './pages/PilotEvidenceDashboard';


// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Protected Route Component
const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  layout?: React.ComponentType<{ children: React.ReactNode }>;
}> = ({ children, layout: LayoutComponent = Layout }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <LayoutComponent>{children}</LayoutComponent>;
};

// /profile is shared across every role, so its shell follows the viewer's
// own role rather than the route — a councillor/contractor should never
// bounce back into the admin chrome just by editing their profile.
const ProfileShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  if (user?.role === 'councillor') return <CouncillorLayout>{children}</CouncillorLayout>;
  if (user?.role === 'contractor') return <ContractorLayout>{children}</ContractorLayout>;
  return <Layout>{children}</Layout>;
};

// Landing route per role: portal roles skip the admin dashboard.
const ROLE_HOME: Record<string, string> = {
  councillor: '/councillor',
  contractor: '/contractor-portal',
};

const RoleHome: React.FC = () => {
  const { user } = useAuth();
  return <Navigate to={ROLE_HOME[user?.role || ''] || '/live-dashboard'} replace />;
};

// Platform console routes are gated by a wholly separate auth context/token
// from the tenant admin portal above — a platform operator session and a
// municipality admin session can coexist without interfering.
const ProtectedPlatformRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = usePlatformAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/platform-ops/login" replace />;
  }

  return <PlatformLayout>{children}</PlatformLayout>;
};

const PublicPlatformRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = usePlatformAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/platform-ops" replace />;
  }

  return <>{children}</>;
};

// Public Route Component (redirects if already authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <RoleHome />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/service-status"
        element={<PublicServiceStatus />}
      />
      <Route
        path="/service-status/:code"
        element={<PublicServiceStatus />}
      />
      <Route
        path="/find-municipality"
        element={<FindMunicipality />}
      />

      {/* Platform console — separate auth boundary from the tenant portal below */}
      <Route
        path="/platform-ops/login"
        element={
          <PublicPlatformRoute>
            <PlatformLogin />
          </PublicPlatformRoute>
        }
      />
      <Route
        path="/platform-ops"
        element={
          <ProtectedPlatformRoute>
            <PlatformDashboard />
          </ProtectedPlatformRoute>
        }
      />
      <Route
        path="/platform-ops/municipalities/:id"
        element={
          <ProtectedPlatformRoute>
            <PlatformMunicipalityDetail />
          </ProtectedPlatformRoute>
        }
      />
      <Route
        path="/platform-ops/billing"
        element={
          <ProtectedPlatformRoute>
            <PlatformBilling />
          </ProtectedPlatformRoute>
        }
      />
      <Route
        path="/platform-ops/audit-log"
        element={
          <ProtectedPlatformRoute>
            <PlatformAuditLog />
          </ProtectedPlatformRoute>
        }
      />

      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <AdminRegister />
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      {/* /dashboard route unmounted alongside the import above — restore both to bring it back */}
      <Route path="/pilot-evidence" element={<ProtectedRoute><PilotEvidenceDashboard /></ProtectedRoute>} />

      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <Users />
          </ProtectedRoute>
        }
      />

      <Route
        path="/residents"
        element={
          <ProtectedRoute>
            <Residents />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        }
      />

      <Route
        path="/categories"
        element={
          <ProtectedRoute>
            <Categories />
          </ProtectedRoute>
        }
      />

      <Route
        path="/departments"
        element={
          <ProtectedRoute>
            <Departments />
          </ProtectedRoute>
        }
      />



      <Route
        path="/escalations"
        element={
          <ProtectedRoute>
            <Escalations />
          </ProtectedRoute>
        }
      />

      <Route
        path="/map"
        element={
          <ProtectedRoute>
            <Map />
          </ProtectedRoute>
        }
      />

      <Route
        path="/messaging"
        element={
          <ProtectedRoute>
            <Messaging />
          </ProtectedRoute>
        }
      />



      <Route
        path="/gis-analytics"
        element={
          <ProtectedRoute>
            <GISAnalytics />
          </ProtectedRoute>
        }
      />

      <Route
        path="/live-dashboard"
        element={
          <ProtectedRoute>
            <LiveDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/technician-locations"
        element={
          <ProtectedRoute>
            <TechnicianLocations />
          </ProtectedRoute>
        }
      />

      <Route
        path="/push-notifications"
        element={
          <ProtectedRoute>
            <PushNotifications />
          </ProtectedRoute>
        }
      />

      <Route
        path="/audit-trail"
        element={
          <ProtectedRoute>
            <AuditTrail />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feedback-system"
        element={
          <ProtectedRoute>
            <FeedbackSystem />
          </ProtectedRoute>
        }
      />



      <Route
        path="/assignments"
        element={
          <ProtectedRoute>
            <Assignments />
          </ProtectedRoute>
        }
      />

      <Route
        path="/resolution-reviews"
        element={<ProtectedRoute><ResolutionReviews /></ProtectedRoute>}
      />
      <Route
        path="/contractor-performance"
        element={<ProtectedRoute><ContractorPerformance /></ProtectedRoute>}
      />
      <Route
        path="/service-delivery"
        element={<ProtectedRoute><ServiceDeliveryHub /></ProtectedRoute>}
      />
      <Route
        path="/councillor"
        element={<ProtectedRoute layout={CouncillorLayout}><WardCouncillorPortal /></ProtectedRoute>}
      />
      <Route
        path="/contractor-portal"
        element={<ProtectedRoute layout={ContractorLayout}><ContractorPortal /></ProtectedRoute>}
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute layout={ProfileShell}>
            <Profile />
          </ProtectedRoute>
        }
      />

      <Route
        path="/user-sessions"
        element={
          <ProtectedRoute>
            <UserSessions />
          </ProtectedRoute>
        }
      />

      <Route
        path="/council-packs"
        element={
          <ProtectedRoute>
            <CouncilPacks />
          </ProtectedRoute>
        }
      />

      <Route
        path="/compliance-reports"
        element={
          <ProtectedRoute>
            <ComplianceReports />
          </ProtectedRoute>
        }
      />

      

      {/* Redirect root to dashboard */}
      <Route
        path="/reports/:id"
        element={
          <ProtectedRoute>
            <CaseWorkspace />
          </ProtectedRoute>
        }
      />

      <Route path="/" element={<RoleHome />} />

      {/* Catch all route */}
      <Route path="*" element={<RoleHome />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <PlatformAuthProvider>
            <Router>
              <AppRoutes />
            </Router>
          </PlatformAuthProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
};

export default App;
